import { FirestoreCollections, IDPrefixes } from '@griply/types';
import { getPrefixIdRange } from '@griply/utils';

import { FirestoreInstance, WhereFilterOp } from '../types/firestore';

// The owner kinds kept live. Task-owned docs are one per linked task — thousands at scale — and are read per task instead.
export const subscribedRelationshipOwners = [IDPrefixes.Goal, IDPrefixes.Tag];

export type GetRelationshipsQueryOptions<
  TCollection = unknown,
  TQuery = unknown,
  TConstraint = unknown,
  TFieldPath = unknown,
  TDb = FirestoreInstance,
> = {
  db: TDb;
  collection: (
    db: NoInfer<TDb>,
    path: string,
    ...rest: string[]
  ) => TCollection;
  query: (ref: TCollection, ...constraints: TConstraint[]) => TQuery;
  where: (
    field: string | NoInfer<TFieldPath>,
    op: WhereFilterOp,
    value: unknown,
  ) => TConstraint;
  documentId: () => TFieldPath;
  userId: string;
  owner: IDPrefixes;
};

// One owner kind per query: a documentId() range on the id prefix next to the roles filter — no orderBy, a range on __name__ must lead.
export const getRelationshipsQuery = <
  TCollection,
  TQuery,
  TConstraint,
  TFieldPath,
  TDb = FirestoreInstance,
>(
  options: GetRelationshipsQueryOptions<
    TCollection,
    TQuery,
    TConstraint,
    TFieldPath,
    TDb
  >,
): TQuery => {
  const { db, collection, query, where, documentId, userId, owner } = options;
  const { start, end } = getPrefixIdRange(owner);

  return query(
    collection(db, FirestoreCollections.Relationships),
    where('roles.all', 'array-contains', userId),
    where(documentId(), '>=', start),
    where(documentId(), '<', end),
  );
};
